import { filterControlClass } from '@/components/admin/versioned/filter-field';
import { useIsVitrine } from '@/lib/use-instance';
import type { VersionedWording } from '@/lib/versioned-labels';
import type { VersionedFilters } from '@matter_extractor/shared';
import { useId } from 'react';
import type { ReactNode } from 'react';

/**
 * « Plus de filtres » - the dimensions that answer a question somebody arrived
 * with (refonte modulaire §4).
 *
 * The object's OWN filters, then four common controls and four boxes:
 *
 *   - the **object's own slot**, first, because it is what somebody opened the
 *     panel for: the number of examples for a criterion (§6), the presentation
 *     and the cran for a persona (§10);
 *   - the two **periods**, created and last version, both bounds inclusive;
 *   - **« Afficher les retirés »**, which is the only box that ADDS rows rather
 *     than removing them - hence the count beside it, so the eye knows there is
 *     something to reveal before ticking;
 *   - **« Utilisé nulle part »**, **« Brouillon en attente »** and
 *     **« Développement en avance sur la production »**, the three questions a
 *     card cannot be scanned for: each needs a comparison, and
 *     `versionedCardShape` carries the two computed ones server-side for
 *     exactly that reason.
 *
 * The panel sits on `gray-50` and not on white, because it is a second surface
 * over the page's ground; the charte allows two levels of white and no more,
 * and `gray-200` or darker is never a surface at all.
 *
 * The object's filters are a `ReactNode` and not a description the layer would
 * render: they are form controls, and a select, a slider and a pair of radios
 * do not share a shape worth naming. They are laid out INSIDE this panel's row
 * so they align with the periods beside them - which is why the slot receives
 * nothing and simply sits first.
 */

export interface FilterPanelProps<Filters extends VersionedFilters> {
  wording: VersionedWording;
  filters: Filters;
  /** The object's own filter controls, drawn at the head of the row. */
  ownFilters: ReactNode;
  /** How many entities are retired - the « (1) » beside the box. */
  retiredCount: number;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
  /** Greyed out when nothing is armed, so the link never promises a no-op. */
  resetDisabled: boolean;
}

/** One checkbox, with the box drawn by the browser and only the ink themed. */
function FilterBox({
  label,
  checked,
  onChange,
  testId,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  testId: string;
  hint?: ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-gray-700 text-sm">
      {/*
        `accent-color` and not a hand-drawn box: a real `<input type=checkbox>`
        answers Space, reports its state to assistive tech and is what
        `getByRole('checkbox')` finds. The maquette draws the box because an
        artboard has no form controls, not because the control should be faked.
      */}
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        className="size-4 accent-bleu-nuit"
      />
      <span>{label}</span>
      {hint}
    </label>
  );
}

/** One inclusive period: « du ... au ... », either bound optional. */
function PeriodField({
  label,
  from,
  to,
  onFrom,
  onTo,
  testId,
}: {
  label: string;
  from: string | null;
  to: string | null;
  onFrom: (next: string | null) => void;
  onTo: (next: string | null) => void;
  testId: string;
}) {
  const fromId = useId();
  const toId = useId();
  return (
    <div className="flex max-w-full flex-col gap-1">
      <span className="font-semibold text-gray-600 text-xs uppercase tracking-wide">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={fromId} className="text-gray-700 text-sm">
          du
        </label>
        <input
          id={fromId}
          data-testid={`${testId}-du`}
          type="date"
          value={from ?? ''}
          onChange={(event) =>
            onFrom(event.currentTarget.value === '' ? null : event.currentTarget.value)
          }
          className={filterControlClass(from !== null, 'w-35')}
        />
        <label htmlFor={toId} className="text-gray-700 text-sm">
          au
        </label>
        <input
          id={toId}
          data-testid={`${testId}-au`}
          type="date"
          value={to ?? ''}
          onChange={(event) =>
            onTo(event.currentTarget.value === '' ? null : event.currentTarget.value)
          }
          className={filterControlClass(to !== null, 'w-35')}
        />
      </div>
    </div>
  );
}

export function FilterPanel<Filters extends VersionedFilters>({
  wording,
  filters,
  ownFilters,
  retiredCount,
  onChange,
  onReset,
  resetDisabled,
}: FilterPanelProps<Filters>) {
  /*
    Every patch below is a slice of the COMMON filters, which every object's
    type carries - but TypeScript cannot see that through an unresolved type
    parameter, so each is widened once. The cast pays for the generic and for
    nothing an object could disagree about.
  */
  const patch = (next: Partial<VersionedFilters>) => onChange(next as Partial<Filters>);
  // Two filters of the atelier's drafts and pointers: the vitrine has neither.
  const vitrine = useIsVitrine();

  return (
    <div
      data-testid={`${wording.list}-filtres-panneau`}
      className="flex flex-col gap-3.5 rounded-md border border-gray-100 bg-gray-50 p-4"
    >
      <div className="flex flex-wrap items-end gap-6">
        {ownFilters}

        <PeriodField
          label="Créé"
          testId={`${wording.entity}-filtre-cree`}
          from={filters.createdFrom}
          to={filters.createdTo}
          onFrom={(createdFrom) => patch({ createdFrom })}
          onTo={(createdTo) => patch({ createdTo })}
        />

        <PeriodField
          label="Dernière version"
          testId={`${wording.entity}-filtre-version`}
          from={filters.versionFrom}
          to={filters.versionTo}
          onFrom={(versionFrom) => patch({ versionFrom })}
          onTo={(versionTo) => patch({ versionTo })}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-6">
          <FilterBox
            label="Afficher les retirés"
            testId={`${wording.entity}-filtre-retires`}
            checked={filters.showRetired}
            onChange={(showRetired) => patch({ showRetired })}
            hint={
              retiredCount === 0 ? undefined : (
                <span className="text-gray-500">({retiredCount})</span>
              )
            }
          />
          <FilterBox
            label="Utilisé nulle part"
            testId={`${wording.entity}-filtre-inutilise`}
            checked={filters.unusedOnly}
            onChange={(unusedOnly) => patch({ unusedOnly })}
          />
          {vitrine ? null : (
            <>
              <FilterBox
                label="Brouillon en attente"
                testId={`${wording.entity}-filtre-brouillon`}
                checked={filters.pendingDraftOnly}
                onChange={(pendingDraftOnly) => patch({ pendingDraftOnly })}
              />
              <FilterBox
                label="Latest dev en avance sur latest prod"
                testId={`${wording.entity}-filtre-en-avance`}
                checked={filters.developmentAheadOnly}
                onChange={(developmentAheadOnly) => patch({ developmentAheadOnly })}
              />
            </>
          )}
        </div>
        <button
          type="button"
          data-testid={`${wording.list}-reinitialiser`}
          disabled={resetDisabled}
          onClick={onReset}
          className="cursor-pointer whitespace-nowrap text-bleu-nuit text-sm underline underline-offset-2 hover:text-bleu-nuit-deep disabled:cursor-not-allowed disabled:text-gray-500 disabled:no-underline"
        >
          Réinitialiser les filtres
        </button>
      </div>
    </div>
  );
}
